import LanguageSelect from '../language-select';
import LookupForm from '../lookup-form';
import DynamicButton from '../dynamic-button';
import AccountSelection from '../account-selection';
import { ComponentDefinition } from '../../lib/typegen/component-definition';
import { KeyValue } from '../../lib/typegen/key-value';
import { ComponentType } from '../../lib/typegen/component-type';
import { LookupOption } from '../../lib/typegen/lookup-option';
import Confirm from '../confirm';
import Balances from '../balances';
import PaymentMethods from '../payment-methods';
import PaymentCash from '../payment-cash';
import { useStore } from '../../lib/hooks/useStore';
import PaymentCheck from '../payment-check';
import PayorInformationForm from '../payor-information-form';
import ThankYou from '../thank-you';
import logger from '../../lib/helpers/logger';
import PaymentCardMagTek from '../payment-card-magtek';
import ReceiptPrintView from '../receipt-print-view';
import ReceiptDigitalView from '../receipt-digital-view';
import { store } from '../../lib/stores/store';
import ScrollableText from '../scrollable-text';
import replaceTemplateText from '../../lib/helpers/replaceTemplateText';
import { handleReceiptOptions } from './handlers/handleReceipt';
import { LookupType } from '../../lib/typegen/lookup-type';
import { useEffect } from 'react';
import PaymentCard from '../payment-card';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import PaymentCardEmvMock from '../payment-card-emv-mock';
import PaymentCardPaygo from '../payment-card-paygo';
import PaymentCardToken from '../payment-card-token';
import SubTitle from '../sub-title';
import MessageBox from '../message-box';
import KioAppHelper from '../../lib/helpers/kioApp';
import pageIds from '../../lib/constants/page-ids';
import PaymentCheckWithManualEntry from '../payment-check-with-manual-entry';

// Dynamically import all assets
const assets = import.meta.glob<{ default: string }>('../../assets/*', { eager: true });

enum BalanceView {
	Edit,
	Select,
	Set,
	Confirm,
}

export default function ComponentRenderer({
	component,
	handleNavigation,
	pageParams,
}: {
	component: ComponentDefinition;
	handleNavigation: (pageId: string, params?: KeyValue[] | null) => void;
	pageParams?: KeyValue[];
}) {
	const {
		configStore: { getString, ppType },
		accountStore: { setBarcodeData },
		uiStore: { setBalancesViewState, setOverrideConfirmView },
	} = useStore();

	// Register window.handleBarcodeScan handler (invoked by ApiService via KioApp.InjectJavaScript)
	useEffect(() => {
		window.handleBarcodeScan = (barcode: string) => {
			if (store.kioskStore.useGlobalBarcodeScanner) {
				const pages = store.configStore.config?.pages ?? [];
				const currentIndex = pages.findIndex((p: { id: string }) => p.id === store.configStore.currentPageId);
				const lookupIndex = pages.findIndex((p: { id: string }) => p.id === pageIds.lookupAccount);
				if (lookupIndex === -1 || currentIndex > lookupIndex) return;
				if (currentIndex === lookupIndex) {
					setBarcodeData(barcode);
					return;
				}
				logger.logBarcodeScanner.log({ barcodeString: barcode }, `Global barcode scan: ${barcode}`);
				store.uiStore.setPendingEarlyBarcodeScan(barcode);
				if (store.configStore.currentPageId === pageIds.languages) return;
				if (!store.uiStore.isAgreedToTerms) {
					store.uiStore.handleNavigation(pageIds.termsAndConditions);
				} else {
					store.uiStore.handleNavigation(pageIds.findYourAccount);
				}
			} else {
				if (store.configStore.currentPageId === pageIds.lookupAccount) {
					setBarcodeData(barcode);
				}
			}
		};
		return () => {
			delete window.handleBarcodeScan;
		};
	}, [setBarcodeData]);

	useEffect(() => {
		const input = document.getElementsByTagName('input')[0];
		if (input) input.focus();
	}, []);

	useEffect(() => {
		setTimeout(() => {
			const input = Array.from(document.getElementsByTagName('input')).filter((input) => input.type !== 'hidden')[0];
			if (input) {
				input.focus();
				if (input.name.includes('account-number') && !store.configStore.config.isAccountNumberAlphanumeric) {
					KioAppHelper('StartKeyboard', '', '', 'number', false, '', '');
				} else {
					// KioWare picks its keyboard layout from elType and never sees inputMode, so a
					// numeric-mode text input (FieldType.Number in lookup-form) has to ask for the
					// numpad explicitly — otherwise it gets the full alpha keyboard.
					const elType = input.inputMode === 'numeric' ? 'number' : input.type;
					KioAppHelper('StartKeyboard', '', '', elType, false, '', '');
				}
			} else {
				KioAppHelper('CloseKeyboard', 0);
			}
		}, 100);
	}, []);

	if (component.type !== ComponentType.Balances) {
		setBalancesViewState(BalanceView.Edit);
	}

	switch (component.type) {
		case ComponentType.LanguageSelect:
			return <LanguageSelect languages={component.data?.languages} handleNavigation={handleNavigation} />;
		case ComponentType.Text:
			if (pageParams) {
				const lookupParams = pageParams ? [...pageParams] : [];
				const lookupType = Number(lookupParams.find((param) => param.key === 'lookupType')?.value);
				if (lookupType === LookupType.Barcode) return;
				if (lookupType === LookupType.BarcodeWithLastName && !lookupParams.find((param) => param.key === 'scannedAccountNumber')) return;
			}
			if (component.data?.textContent) {
				const text = getString(component.data.textContent, component.data?.noTranslate);
				if (store.configStore.currentPageId === pageIds.termsAndConditions) {
					return <ScrollableText textContent={text} compact />;
				}
				return <article style={{ textAlign: 'center' }}>{text}</article>;
			}
			break;
		case ComponentType.Image:
			if (component.data?.target) {
				const assetPath = `../../assets/${component.data.target}`;
				const imageModule = assets[assetPath];

				if (imageModule?.default) {
					return (
						<img
							src={imageModule.default}
							alt={component.data.textContent || ''}
							width={component.data.width}
							height={component.data.height}
							style={{ display: 'block', margin: '0 auto' }}
						/>
					);
				} else {
					logger.logGeneral.error({}, `Image not found: ${component.data.target}`, 'ComponentRenderer');
				}
			}
			break;
		case ComponentType.SubTitle:
			if (component.data?.textContent) {
				return <SubTitle content={getString(component.data?.textContent)} />;
			}
			break;
		case ComponentType.ScrollableText:
			if (component.data?.textContent) {
				const text = replaceTemplateText(component.data?.textContent ? getString(component.data?.textContent) : '');
				return <ScrollableText textContent={text} />;
			}
			break;
		case ComponentType.Button:
			if (
				component.data?.action !== undefined &&
				(handleReceiptOptions(component.data?.params) === 'not-a-receipt-option' ||
					handleReceiptOptions(component.data?.params) === 'available')
			) {
				return <DynamicButton action={component.data?.action} data={component.data} handleNavigation={handleNavigation} />;
			}
			break;
		case ComponentType.MessageBox:
			return (
				<MessageBox
					type={(component.data?.type as 'info' | 'warning') || 'info'}
					dangerouslySetInnerHTML={{ __html: getString(component.data?.textContent, component.data?.noTranslate) || '' }}
				/>
			);
		case ComponentType.LookupForm:
			setOverrideConfirmView(true);
			if (pageParams) {
				const lookupParams = pageParams ? [...pageParams] : [];
				const lookupType = Number(lookupParams.find((param) => param.key === 'lookupType')?.value);
				const scannedAccountNumber = lookupParams.find((param) => param.key === 'scannedAccountNumber')?.value;

				let option: LookupOption | undefined;
				if (scannedAccountNumber) {
					// After barcode scan, find the option with a form (not the scanner option)
					option = component.data?.lookupOptions?.find((opt: LookupOption) => opt.lookupType === lookupType && opt.form?.fields?.length);
				}
				if (!option) {
					option = component.data?.lookupOptions?.find((opt: LookupOption) => opt.lookupType === lookupType);
				}
				if (!option || Number.isNaN(lookupType)) throw new Error(`Lookup option not found for lookup type ${lookupType}`);

				const defaultValues = scannedAccountNumber ? { 'account-number': scannedAccountNumber } : undefined;
				return (
					<LookupForm
						type={option.lookupType}
						title={option.title}
						submitLabel={option.form?.submitLabel}
						form={option.form}
						handleNavigation={handleNavigation}
						requestType={option.accountLookupRequestType}
						defaultValues={defaultValues}
					/>
				);
			}
			break;
		case ComponentType.AccountSelection:
			if (component.data?.accountServerProps) return <AccountSelection {...component.data.accountServerProps} />;
			break;
		case ComponentType.Confirm: {
			if (component.data?.confirmServerProps) return <Confirm {...component.data?.confirmServerProps} />;
			break;
		}
		case ComponentType.Balances:
			if (component.data?.balancesServerProps) return <Balances {...component.data.balancesServerProps} />;
			break;
		case ComponentType.PayorInformationForm:
			return <PayorInformationForm form={component.data?.form} handleNavigation={handleNavigation} />;
		case ComponentType.PaymentMethods:
			if (component.data?.paymentMethodServerProps) {
				if (component.data.paymentMethodServerProps.cash?.isEnabled) {
					component.data.paymentMethodServerProps.cash.isEnabled =
						store.accountStore.cashEnabled && component.data.paymentMethodServerProps.cash.isEnabled;
				}
				if (component.data.paymentMethodServerProps.card?.isEnabled) {
					component.data.paymentMethodServerProps.card.isEnabled =
						store.accountStore.cardEnabled && component.data.paymentMethodServerProps.card.isEnabled;
				}
				if (component.data.paymentMethodServerProps.check?.isEnabled) {
					component.data.paymentMethodServerProps.check.isEnabled =
						store.accountStore.checkEnabled && component.data.paymentMethodServerProps.check.isEnabled;
				}
				return <PaymentMethods {...component.data?.paymentMethodServerProps} />;
			}
			break;
		case ComponentType.CashPayment:
			if (component.data?.paymentCashServerProps) return <PaymentCash {...component.data?.paymentCashServerProps} />;
			break;
		case ComponentType.CheckPayment:
			if (component.data?.paymentCheckServerProps) return <PaymentCheck {...component.data?.paymentCheckServerProps} />;
			break;
		case ComponentType.CheckPaymentWithManualEntry:
			if (component.data?.paymentCheckServerProps) return <PaymentCheckWithManualEntry {...component.data?.paymentCheckServerProps} />;
			break;
		case ComponentType.CardPaymentMagTek:
			if (component.data?.paymentCardServerProps) return <PaymentCardMagTek {...component.data?.paymentCardServerProps} />;
			break;
		case ComponentType.CardPaymentEMV:
			if (component.data?.paymentCardServerProps) {
				if (ppType === PPConfigType.PayGo || ppType === PPConfigType.MyUsage)
					return <PaymentCardPaygo {...component.data?.paymentCardServerProps} />;
				return <PaymentCard {...component.data?.paymentCardServerProps} />;
			}
			break;
		case ComponentType.CardPaymentEmvMock:
			if (component.data?.paymentCardServerProps) return <PaymentCardEmvMock {...component.data?.paymentCardServerProps} />;
			break;
		case ComponentType.CardPaymentToken:
			if (component.data?.paymentCardServerProps) return <PaymentCardToken {...component.data?.paymentCardServerProps} />;
			break;
		case ComponentType.ReceiptPrint:
			if (component.data?.receiptPrintServerProps && component.data?.target)
				return <ReceiptPrintView receiptServerProps={component.data?.receiptPrintServerProps} nextPageTarget={component.data.target} />;
			break;
		case ComponentType.ReceiptDigital: {
			let receiptType = 0;
			const receiptParams = pageParams ? [...pageParams] : [];
			if (receiptParams && component.data?.digitalReceiptViewServerProps) {
				const num = receiptParams.find((param) => param.key === 'receiptType')?.value;
				receiptType = Number(num);
				return (
					<ReceiptDigitalView
						key={receiptType}
						receiptType={receiptType}
						digitalReceiptViewServerProps={component.data.digitalReceiptViewServerProps}
					/>
				);
			}
			break;
		}
		case ComponentType.ThankYou:
			return <ThankYou />;
		default:
			logger.logGeneral.error({}, `No component found for type ${component.type}`, 'ComponentRenderer');
	}
}
